<script lang="ts">import FieldLabel from '../FieldLabel/FieldLabel.svelte';
import SelectDropdown from '../SelectDropdown/SelectDropdown.svelte';
import ValidationMessage from '../ValidationMessage/ValidationMessage.svelte';
// Need to use let because invalid, selectedValue, and selectedValues are changed and you cannot change a constant and
// you cannot use the $props() rune twice
/* eslint-disable prefer-const */
let { colorMode = 'light', disabled = false, id = '', invalid = $bindable(false), items, label = '', name, placeholder = '', readonly = false, required = false, validationMessage = '', showSelectedItemIcon = false, selectedValue = $bindable(''), selectedValues = $bindable([]), multiSelect = false, onClick = () => { }, onSelect = () => { }, class: className = '', ...rest } = $props();
/* eslint-enable prefer-const */
let displayInput;
let isOpen = $state(false);
let openedByKeyboard = $state(false);
function handleContainerClick(e) {
    e.stopPropagation();
    const target = e.currentTarget;
    // If this is a Mac VoiceOver click using Control + Option + Shift, then the element is still :focus-visible
    openedByKeyboard = target.matches(':focus-visible');
    isOpen = !isOpen;
    // Only call the onClick when the SelectDropdown is open
    isOpen && onClick?.();
}
function handleLabelClick(e) {
    e.stopPropagation();
    displayInput.focus();
    !isOpen && handleContainerClick(new Event('click'));
}
// Handle keydown events on the display trigger.
function handleInputKeydown(e) {
    switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case ' ': {
            e.preventDefault();
            openedByKeyboard = true;
            isOpen = true;
            onClick?.();
            break;
        }
        case 'Escape':
        case 'Tab': {
            isOpen = false;
            break;
        }
        default: {
            break;
        }
    }
}
function handleDropdownSelect(indices) {
    if (multiSelect) {
        // Sort to match the original order in the SelectDropdown instead of the order the items were clicked
        const sortedIndices = [...indices].sort((a, b) => a - b);
        selectedValues = sortedIndices.map((index) => items[index]?.value).filter(Boolean);
        onSelect?.(sortedIndices);
    }
    else {
        const index = indices;
        selectedValue = items[index]?.value;
        handleDropdownExit();
        onSelect?.(index);
    }
}
function handleDropdownExit() {
    isOpen = false;
    displayInput.focus();
}
function handleOutsideSelectClick(event) {
    // SelectDropdown should not be closed if the click is inside FieldLabel. The handleLabelClick function handles
    // opening and closing the SelectDropdown
    const isClickInsideFieldLabel = event.target.closest('.SelectField .FieldLabel') !== null;
    // SelectDropdown should not be closed if the click is inside SelectDropdown. The handleDropdownSelectOrExit
    // function handles clicks inside SelectDropdown
    const isClickInsideSelectDropdown = event.target.closest('.SelectDropdown') !== null;
    if (!isClickInsideSelectDropdown && !isClickInsideFieldLabel) {
        isOpen = false;
    }
}
function deriveSelectedIndex(multiSelect, selectedValues, selectedValue, items) {
    if (multiSelect) {
        return items
            .map((item, index) => ((selectedValues ?? []).includes(item.value) ? index : -1))
            .filter((index) => index !== -1);
    }
    else {
        return items.findIndex((item) => item.value === selectedValue);
    }
}
const selectedIndex = $derived(deriveSelectedIndex(multiSelect, selectedValues, selectedValue, items));
// selectedItem/selectedValue is used for SelectField or when there is only one option selectable
const selectedItem = $derived(multiSelect ? undefined : items[selectedIndex]);
// selectedItems/selectedValue is used for MultiSelectField or when there multiple options selectable
const selectedItems = $derived(multiSelect && Array.isArray(selectedIndex) ? selectedIndex.sort((a, b) => a - b).map((index) => items[index]) : []);
const displayValue = $derived(multiSelect ? selectedItems.map((item) => item.label).join(', ') : selectedItem?.label || '');
// Due to binding behavior, this instantiation must be done separate from declaration for default value to work
// properly
if (multiSelect && !selectedValues?.length) {
    selectedValues = items.filter((item) => item.selected).map((item) => item.value);
}
// Due to binding behavior, this instantiation must be done separate from declaration for default value to work
// properly
if (!multiSelect && !selectedValue) {
    selectedValue = items.find((item) => item.selected)?.value ?? '';
}
</script>

<svelte:window on:click={handleOutsideSelectClick} />

<div {...rest} class="SelectField {className}" aria-disabled={disabled || readonly} data-color-mode={colorMode}>
  {#if label}
    <FieldLabel for={name} id="{name}-label" {disabled} text={label} onclick={handleLabelClick} />
  {/if}

  <div class="SelectField__wrapper">
    <div
      class="SelectField__container"
      id={id || name}
      role="combobox"
      aria-labelledby={label ? `${name}-label` : undefined}
      aria-label={label ? undefined : placeholder}
      aria-controls="{name}-dropdown"
      aria-expanded={isOpen}
      aria-invalid={invalid}
      aria-describedby={invalid ? `selectfield-error-${name}` : undefined}
      aria-required={required}
      tabindex={disabled ? -1 : 0}
      onkeydown={handleInputKeydown}
      onclick={handleContainerClick}
      bind:this={displayInput}
    >
      <div class="SelectField__subcontainer">
        <span class="SelectField__text" class:SelectField__text--placeholder={!displayValue}>
          {displayValue || placeholder}
        </span>

        {#if !multiSelect && showSelectedItemIcon && selectedItem?.icon}
          <span class="SelectField__icon">
            <selectedItem.icon />
          </span>
        {/if}
      </div>

      <svg class="SelectField__arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8.70703 11.7071C8.31651 12.0976 7.68334 12.0976 7.29282 11.7071L1.29282 5.70711L2.70703 4.29289L7.99992 9.58579L13.2928 4.29289L14.707 5.70711L8.70703 11.7071Z"
        />
      </svg>
    </div>

    <SelectDropdown
      {selectedIndex}
      {isOpen}
      {items}
      {name}
      {colorMode}
      {multiSelect}
      defaultFocusFirstOption={openedByKeyboard}
      onSelect={(value) => handleDropdownSelect(value)}
      onExit={handleDropdownExit}
    />
  </div>

  {#if multiSelect}
    <div class="SelectField__checkboxes">
      <!-- Since MultiSelectField has a name it can be treated as a regular checkbox input, so options don't have to be
       added and removed from the DOM when selected -->
      {#each items as item, index}
        <input
          type="checkbox"
          {name}
          value={item.value}
          checked={Array.isArray(selectedIndex) && selectedIndex.includes(index)}
          {disabled}
        />
      {/each}
    </div>
  {:else}
    <input type="hidden" value={selectedItem?.value ?? null} {name} />
  {/if}

  {#if validationMessage && invalid}
    <ValidationMessage id="selectfield-error-{name}" text={validationMessage} {colorMode} />
  {/if}
</div>

<style>.SelectField__text {
  font-weight: var(--form-field-input-font-weight);
  font-size: var(--form-field-input-font-size);
  letter-spacing: var(--form-field-input-letter-spacing);
  line-height: var(--form-field-input-line-height);
}

/**
 * Checkbox styling is shared between CheckboxesField and MultiSelectField
 * The biggest difference between the two is the hover/focus/etc states are on different elements, so it cannot just be
 * one big mixin and has to be broken up
 */
@layer design-system-components.pwds {
  .SelectField {
    --color: var(--color-text);
  }
  .SelectField[aria-disabled=true] {
    --form-field-inset-box-shadow-size: 1px;
    --form-field-inset-box-shadow-color: var(--color-form-field-disabled-box-shadow);
    pointer-events: none;
  }
  .SelectField[aria-disabled=true] .SelectField__container {
    box-shadow: inset 0 0 0 var(--form-field-inset-box-shadow-size) var(--form-field-inset-box-shadow-color);
    background-color: var(--color-form-field-disabled-background-color);
  }
  .SelectField__wrapper {
    position: relative;
  }
  .SelectField__container {
    --form-field-inset-box-shadow-size: 1px;
    --form-field-inset-box-shadow-color: var(--color-form-field-default);
    box-shadow: inset 0 0 0 var(--form-field-inset-box-shadow-size) var(--form-field-inset-box-shadow-color);
    position: relative;
    height: 45px;
    padding: var(--form-field-input-padding);
    padding-right: calc(32px + var(--space-10));
    background-color: var(--color-background);
    border-radius: var(--form-field-input-border-radius);
    cursor: pointer;
    outline: none;
  }
  .SelectField__container:hover {
    --form-field-inset-box-shadow-size: 1px;
    --form-field-inset-box-shadow-color: var(--color-form-field-hover);
  }
  .SelectField__container:focus, .SelectField__container:focus-within {
    --form-field-inset-box-shadow-size: 2px;
    --form-field-inset-box-shadow-color: var(--color-form-field-focus);
  }
  .SelectField__container[aria-invalid=true] {
    --form-field-inset-box-shadow-color: var(--color-critical);
  }
  .SelectField__container[aria-expanded=true] .SelectField__arrow {
    transform: rotate(180deg);
  }
  .SelectField__subcontainer {
    display: flex;
    align-items: center;
    gap: var(--space-10);
  }
  .SelectField__text {
    display: block;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  .SelectField__text--placeholder {
    color: var(--color-form-field-placeholder);
  }
  .SelectField__icon :global(svg) {
    display: block;
  }
  .SelectField__arrow {
    position: absolute;
    right: var(--select-field-arrow-right-offset);
    top: calc(50% - var(--select-field-arrow-vertical-offset));
    pointer-events: none;
    display: block;
    fill: var(--color-form-field-placeholder);
    transition: transform 0.2s;
  }
  .SelectField__checkboxes,
  .SelectField__checkboxes input[type=checkbox] {
    display: none;
  }
}</style>
